import { MobilityEnum } from '@app/icare/entities/patient/schema/PatientMobility'
import type { PatientForm } from '@app/icare/entities/patientForm/schema/PatientForm'
import { msg } from '@lingui/core/macro'
import { defineEnum } from '@pkg/shared/utils/enum-builder'
import { z } from 'zod'
import f from '../field/f'
import { PatientFormCommonSchema } from './PatientFormCommon'
import { PatientFormTypeEnum } from './PatientFormType'

export const Abnormality = {
  conspicuous: 'conspicuous',
  inconspicuous: 'inconspicuous',
} as const

export type AbnormalityType = (typeof Abnormality)[keyof typeof Abnormality]

export const AbnormalityEnum = defineEnum({
  conspicuous: msg`Auffällig`,
  inconspicuous: msg`Unauffällig`,
})

export const ProgressReportContactType = {
  PHONE: 'PHONE',
  ON_SITE: 'ON_SITE',
} as const

export const ProgressReportContactTypeEnum = defineEnum({
  PHONE: msg`Telefonisch`,
  ON_SITE: msg`Vor Ort`,
})

export type ProgressReportContactTypeType =
  (typeof ProgressReportContactTypeEnum.$.values)[keyof typeof ProgressReportContactTypeEnum.$.values]

export const ProgressReportStatusEnum = defineEnum({
  new: msg`Neu`,
  approved: msg`Abgeschlossen`,
})

export type ProgressReportStatusType = typeof ProgressReportStatusEnum.$.Type

export const ProgressReportTherapyEnum = defineEnum({
  pe: msg`PE`,
  enteralTubeFeeding: msg`Trinknahrung`,
  painTherapy: msg`Schmerztherapie`,
  peg: msg`PEG`,
  pej: msg`PEJ`,
})

export type ProgressReportTherapyType = (typeof ProgressReportTherapyEnum.$.values)[number]

export const ProgressReportAccessOptionEnum = defineEnum({
  port: msg`Port`,
  groshong: msg`Groshong / Broviac`,
  piicc: msg`Picc`,
  zvk: msg`ZVK`,
  butterfly: msg`Butterfly`,
  midline: msg`Midline-Katheter`,
  pvk: msg`pVK`,
  sc: msg`s.c.`,
})

export type ProgressReportAccessOptionType = (typeof ProgressReportAccessOptionEnum.$.values)[number]

export const ProgressReportNeedleOptionEnum = defineEnum({
  gripper: msg`Gripper`,
  surecan: msg`Surecan`,
  intrastick: msg`Intrastick`,
})

export type ProgressReportNeedleOptionType = (typeof ProgressReportNeedleOptionEnum.$.values)[number]

export const ProgressReportDocumentationOptionEnum = defineEnum({
  changeRequiredDueToHygiene: msg`Portnadelwechsel nach Hygienestandard`,
  changeRequiredDueToHygieneForUnderneathNeedle: msg`s.c.-Nadelwechsel nach Hygienestandard`,
  bandageChange: msg`Verbandwechsel`,
})

export type ProgressReportDocumentationOptionType = (typeof ProgressReportDocumentationOptionEnum.$.values)[number]

export const ProgressReportNutritionSituationEnum = defineEnum({
  lossOfAppetite: msg`Appetitlosigkeit`,
  nausea: msg`Übelkeit`,
  vomiting: msg`Erbrechen`,
})

export type ProgressReportNutritionSituationType = (typeof ProgressReportNutritionSituationEnum.$.values)[number]

// Keep historical UI/data mapping labels to avoid changing stored semantics.
export const ProgressReportGastrointestinalDisorderEnum = defineEnum({
  diarrhea: msg`Obstipation`,
  obstipation: msg`Diarrhoe`,
})

export type ProgressReportGastrointestinalDisorderType =
  (typeof ProgressReportGastrointestinalDisorderEnum.$.values)[number]

const ProgressReportTherapyShape = ProgressReportTherapyEnum.$.values.reduce(
  (shape, key) => {
    shape[key] = z.boolean().nullish()
    return shape
  },
  {} as Record<ProgressReportTherapyType, z.ZodOptional<z.ZodNullable<z.ZodBoolean>>>,
)

export const ProgressReportTherapySchema = z.object(ProgressReportTherapyShape)

export const ProgressReportFormSchema = PatientFormCommonSchema.extend({
  status: z.enum(ProgressReportStatusEnum.$.zodEnum()).default(ProgressReportStatusEnum.new),
  statusChangedAt: z.iso.datetime().nullish().default(null),
  type: z.literal(PatientFormTypeEnum.ProgressReport).default(PatientFormTypeEnum.ProgressReport),
  form: z.object({
    contact: z.object({
      type: z.enum(ProgressReportContactTypeEnum.$.zodEnum()).nullish(),
      date: f.date().refine(value => !!value, {
        message: 'Field is required',
      }),
    }),
    therapy: ProgressReportTherapySchema.nullish(),
    current: z
      .object({
        weight: f.number().nullish(),
        mobility: z.enum(MobilityEnum.$.zodEnum()).nullish(),
      })
      .nullish(),
    parenteralNutrition: z
      .object({
        active: z.boolean().nullish(),
        timesAWeek: f.number().nullish(),
        pause: z
          .object({
            active: z.boolean().nullish(),
            period: z
              .object({
                startDate: f.date().nullish(),
                endDate: f.date().nullish(),
              })
              .nullish(),
          })
          .nullish(),
        stationary: z
          .object({
            active: z.boolean().nullish(),
            period: z
              .object({
                startDate: f.date().nullish(),
                endDate: f.date().nullish(),
              })
              .nullish(),
          })
          .nullish(),
      })
      .nullish(),
    intravenousAccess: z
      .object({
        port: z.boolean().nullish(),
        groshong: z.boolean().nullish(),
        piicc: z.boolean().nullish(),
        zvk: z.boolean().nullish(),
        butterfly: z.boolean().nullish(),
        catheter: z.boolean().nullish(),
        midline: z.boolean().nullish(),
        pvk: z.boolean().nullish(),
        sc: z.boolean().nullish(),
        comment: z.string().nullish(),
        customNeedleBrand: z.string().nullish(), // deprecated?
        needle: z
          .object({
            surecan: z.boolean().nullish(),
            gripper: z.boolean().nullish(),
            intrastick: z.boolean().nullish(),
            type: z
              .object({
                weight: z.string().nullish(),
                size: z.string().nullish(),
              })
              .nullish(),
            other: z.string().nullish(),
          })
          .nullish(),
        documentation: z
          .object({
            abnormality: z.enum(Abnormality).nullish(),
            changeRequiredDueToHygiene: z.boolean().nullish(),
            changeRequiredDueToHygieneForUnderneathNeedle: z.boolean().nullish(),
            bandageChange: z.boolean().nullish(),
            comment: z.string().nullish(),
          })
          .nullish(),
      })
      .nullish(),
    nutrition: z
      .object({
        lossOfAppetite: z.boolean().nullish(),
        nausea: z.boolean().nullish(),
        vomiting: z.boolean().nullish(),
        comment: z.string().nullish(),
      })
      .nullish(),
    gastrointestinalDisorder: z
      .object({
        obstipation: z.boolean().nullish(),
        diarrhea: z.boolean().nullish(),
        comment: z.string().nullish(),
      })
      .nullish(),
    painTherapy: z
      .object({
        times: f.number().nullish(),
        tries: f.number().nullish(),
        remainingInCassette: f.number().nullish(),
        painLevel: f.number().nullish().default('1'),
      })
      .nullish(),
    comment: z.string().optional(),
  }),
})

export const isProgressReport = (form: PatientForm): form is ProgressReportForm => {
  return form.type === PatientFormTypeEnum.ProgressReport
}

export type ProgressReportForm = z.infer<typeof ProgressReportFormSchema>
